import { firebaseApp } from '../firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  setDoc,
  getDocs,
  onSnapshot,
  Query,
  QuerySnapshot,
  QueryDocumentSnapshot,
  startAfter,
  writeBatch,
  serverTimestamp,
  increment,
  Timestamp,
  CollectionReference,
  DocumentData,
} from 'firebase/firestore';
import { Unsubscribe } from 'firebase/auth';

interface NotificationData extends DocumentData {
  type: string;
  content: string;
  url?: string;
  permanent?: boolean;
  important?: boolean;
  createdAt: Timestamp | string;
  readAt: Timestamp | string;
  deletedAt: Timestamp | string | null;
  isDeleted: boolean;
  isRead: boolean;
}

const QUERY_LIMIT = 50;
const NOTIFICATIONS = 'notifications';
const CREATED_AT = 'createdAt';

type ColProp = 'businesses' | 'users';

const userRef = (col: ColProp, userId: string) =>
  doc<NotificationData>(
    collection(
      getFirestore(firebaseApp),
      col,
    ) as CollectionReference<NotificationData>,
    userId,
  );

const notificationsRef = (col: ColProp, userId: string) =>
  collection(
    userRef(col, userId),
    NOTIFICATIONS,
  ) as CollectionReference<NotificationData>;

const getNotificationsQuery = (
  col: ColProp,
  userId: string,
): Query<NotificationData> => {
  const cur = new Date();
  const createdAt = new Date(
    cur.getFullYear(),
    cur.getMonth() - 1,
    cur.getDate(),
    0,
    0,
    0,
    0,
  );

  return query<NotificationData>(
    notificationsRef(col, userId),
    where('isDeleted', '==', false),
    where(CREATED_AT, '>=', createdAt),
    orderBy(CREATED_AT, 'desc'),
  );
};

export const getNotificationsCount = (
  col: ColProp,
  userId: string,
): Promise<number> => {
  const q = query(
    getNotificationsQuery(col, userId),
    where('isRead', '==', false),
  );
  return getDocs(q).then((doc) => doc.size);
};

export const updateNotificationsCount = (
  col: ColProp,
  userId: string,
  notificationsCount: number,
): Promise<void> => {
  return setDoc(userRef(col, userId), { notificationsCount }, { merge: true });
};

export const listenNotificationsCount = (
  col: ColProp,
  userId: string,
  onChange: (count: number | null) => void,
): Unsubscribe => {
  return onSnapshot(
    doc(collection(getFirestore(firebaseApp), col), userId),
    (snapshot) => {
      const data = snapshot.data();
      onChange(data?.notificationsCount);
    },
  );
};

export const listenNotifications = (
  col: ColProp,
  userId: string,
  onChange: (docs: QuerySnapshot<DocumentData>) => void,
  l = QUERY_LIMIT,
): Unsubscribe => {
  const q = query(getNotificationsQuery(col, userId), limit(l));
  return onSnapshot(q, (snapshot) => onChange(snapshot));
};

export const getNotifications = (
  col: ColProp,
  userId: string,
  lastVisible: QueryDocumentSnapshot | null = null,
  l = QUERY_LIMIT,
): Promise<QueryDocumentSnapshot[]> => {
  let q = getNotificationsQuery(col, userId);
  if (lastVisible != null) {
    q = query(
      q,
      startAfter(lastVisible),
      where(CREATED_AT, '<=', lastVisible.data().createdAt),
      limit(l),
    );
  } else {
    q = query(q, limit(l));
  }
  return getDocs<NotificationData>(q).then((snapshot) => snapshot.docs);
};

export const readNotifications = (
  col: ColProp,
  userId: string,
  docIds: Array<string>,
): Promise<void> => {
  const batch = writeBatch(getFirestore(firebaseApp));

  const readAt = serverTimestamp();

  docIds.forEach((docId) => {
    const docRef = doc(notificationsRef(col, userId), docId);
    batch.update(docRef, { readAt, isRead: true });
  });

  batch.set(
    userRef(col, userId),
    {
      notificationsCount: increment(-docIds.length),
    },
    { merge: true },
  );

  return batch.commit();
};
